import type { ArcElement, Chart, Plugin } from 'chart.js';
import { formatNumber } from './format';
import type { ChartTheme } from './chart-theme';

/**
 * Etiquetas dibujadas SOBRE el gráfico de anillo.
 *
 * ── Por qué no basta el recuadro al pasar el puntero ───────────────────────
 * En un anillo la pregunta es «cuánto de todo es cada parte», y responderla porción
 * por porción con el puntero obliga a recordar la anterior mientras se mira la
 * siguiente. Un anillo se lee de un vistazo o no se lee.
 *
 * ── Por qué NO se etiquetan todas las porciones ────────────────────────────
 * «Dotación por área» tiene 31 áreas y varias con una sola persona. Escribir un
 * porcentaje sobre una porción de dos grados produce texto encimado e ilegible, y
 * un gráfico ilegible es peor que uno sin etiquetas: parece que dice algo.
 *
 * Solo se etiquetan las porciones con espacio real. Las demás siguen accesibles en
 * la leyenda —que ahora trae valor y porcentaje— y en el recuadro del puntero.
 *
 * ── Por qué el total va en el centro ───────────────────────────────────────
 * El agujero del anillo es espacio desperdiciado, y el total es justo el dato que
 * falta para interpretar las proporciones: «20 %» significa cosas distintas sobre
 * 204 personas que sobre 12.
 *
 * No se usa `chartjs-plugin-datalabels`: añade una dependencia para lo que aquí son
 * treinta líneas, y ese complemento etiqueta todo por omisión, que es exactamente lo
 * que hay que evitar con 31 categorías.
 */

/** Ángulo mínimo, en radianes, para que quepa un porcentaje sin encimarse. */
const ANGULO_MINIMO = 0.28;

/** Porcentaje mínimo: por debajo, la cifra estorba más de lo que informa. */
const PORCENTAJE_MINIMO = 4;

export function doughnutLabelsPlugin(theme: ChartTheme): Plugin<'doughnut'> {
  return {
    id: 'oxxeanDoughnutLabels',

    afterDatasetsDraw(chart: Chart<'doughnut'>) {
      const { ctx } = chart;
      const meta = chart.getDatasetMeta(0);
      if (!meta?.data?.length) return;

      const valores = (chart.data.datasets[0]?.data ?? []) as Array<number | null>;
      const total = valores.reduce<number>(
        (suma, v) => suma + (typeof v === 'number' ? v : 0),
        0,
      );
      if (total <= 0) return;

      ctx.save();

      // ── Porcentaje sobre cada porción ────────────────────────────────────
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.font = '600 11px system-ui, -apple-system, "Segoe UI", sans-serif';

      // Los elementos de un anillo son `ArcElement`. El tipo genérico de `meta.data`
      // no lo sabe, y sin la precisión no existe `getCenterPoint`.
      const arcos = meta.data as unknown as ArcElement[];

      arcos.forEach((arco, indice) => {
        const valor = valores[indice];
        if (typeof valor !== 'number' || valor <= 0) return;

        const porcentaje = (valor / total) * 100;
        if (porcentaje < PORCENTAJE_MINIMO) return;

        const props = arco.getProps(['startAngle', 'endAngle'], true);
        if (props.endAngle - props.startAngle < ANGULO_MINIMO) return;

        // `getCenterPoint()` ya devuelve el punto medio de la BANDA del arco: no hay
        // que calcular ángulos ni radios a mano. Hacerlo con trigonometría propia
        // -como estaba- duplica una cuenta que la biblioteca ya hace bien.
        const { x, y } = arco.getCenterPoint(true);

        const texto = `${formatNumber(porcentaje, 'decimal')} %`;

        // Contorno oscuro bajo el texto blanco: las porciones usan seis colores de
        // la paleta y algunos son claros. Sin el contorno el texto desaparece sobre
        // el ámbar y el verde.
        ctx.lineWidth = 3;
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.55)';
        ctx.strokeText(texto, x, y);
        ctx.fillStyle = '#FFFFFF';
        ctx.fillText(texto, x, y);
      });

      // ── Total en el centro ───────────────────────────────────────────────
      const primerArco = arcos[0];
      if (primerArco) {
        const props = primerArco.getProps(['innerRadius', 'x', 'y'], true);
        const cx = props.x;
        const cy = props.y;

        // Solo si el agujero tiene sitio: en un anillo pequeño el total taparía el
        // gráfico en lugar de acompañarlo. `x` e `y` del arco son el centro del
        // ANILLO, distinto del punto medio de la banda de `getCenterPoint()`.
        if (props.innerRadius > 34 && typeof cx === 'number' && typeof cy === 'number') {
          ctx.fillStyle = theme.tick;
          ctx.font = '500 10px system-ui, -apple-system, "Segoe UI", sans-serif';
          ctx.fillText('Total', cx, cy - 9);

          ctx.fillStyle = theme.text;
          ctx.font = '600 17px system-ui, -apple-system, "Segoe UI", sans-serif';
          ctx.fillText(formatNumber(total, 'integer'), cx, cy + 8);
        }
      }

      ctx.restore();
    },
  };
}
